import { getUserProfile } from '@db/services/profile.service';
import * as Sentry from '@sentry/react';
import type { Session, Subscription, User } from '@supabase/supabase-js';
import { create } from 'zustand';
import { supabase } from '@/lib/supabase';

const AUTH_TIMEOUT_MS = 3000;
const TOKEN_EXPIRY_BUFFER_S = 60;

type AuthState = {
  user: User | null;
  session: Session | null;
  profileUserName: string | null;
  profileUserId: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
};

type AuthActions = {
  initialize: () => Promise<void>;
  ensureValidUserId: () => Promise<string | null>;
  refreshSession: () => Promise<void>;
  signInWithGoogle: (redirectTo?: string) => Promise<void>;
  signOut: () => Promise<void>;
  setProfileUserName: (name: string | null) => void;
  setProfileUserId: (id: string | null) => void;
};

let _initPromise: Promise<void> | null = null;
let _subscription: Subscription | null = null;

export const requireClient = () => {
  if (!supabase) throw new Error('Supabaseが初期化されていません');
  return supabase;
};

export const useAuthStore = create<AuthState & AuthActions>((set, get) => {
  const applySession = (session: Session | null, resetProfile = false) =>
    set({
      session,
      user: session?.user ?? null,
      isAuthenticated: !!session,
      ...(resetProfile && { profileUserName: null, profileUserId: null }),
    });

  const loadProfile = async (userId: string) => {
    try {
      const profile = await getUserProfile(requireClient(), userId);
      set({
        profileUserName: profile?.user_name ?? null,
        profileUserId: userId,
      });
    } catch {
      set({ profileUserName: null, profileUserId: null });
    }
  };

  return {
    user: null,
    session: null,
    profileUserName: null,
    profileUserId: null,
    isLoading: true,
    isAuthenticated: false,

    initialize: async () => {
      if (_initPromise) return _initPromise;

      _initPromise = (async () => {
        if (!supabase) {
          set({ isLoading: false });
          return;
        }

        try {
          const {
            data: { session },
            error,
          } = await supabase.auth.getSession();
          if (error) throw error;

          applySession(session, true);
          if (session?.user?.id) {
            await loadProfile(session.user.id);
          }
          set({ isLoading: false });

          _subscription?.unsubscribe();
          const {
            data: { subscription },
          } = supabase.auth.onAuthStateChange(async (event, newSession) => {
            const currentUserId = get().user?.id;
            const isSameUser = newSession?.user?.id === currentUserId;
            const shouldResetProfile =
              event === 'SIGNED_OUT' || (event === 'SIGNED_IN' && !isSameUser);
            applySession(newSession, shouldResetProfile);
            if (newSession?.user?.id && shouldResetProfile) {
              await loadProfile(newSession.user.id);
            }
          });
          _subscription = subscription;
        } catch (error) {
          Sentry.captureException(error, {
            tags: { feature: 'auth_init' },
          });
          set({ isLoading: false });
        }
      })();

      return _initPromise;
    },

    ensureValidUserId: async () => {
      if (!supabase) return null;

      try {
        const result = await Promise.race([
          (async () => {
            const {
              data: { session },
            } = await supabase.auth.getSession();

            const now = Math.floor(Date.now() / 1000);
            const isFresh =
              session?.user?.id &&
              session.expires_at &&
              session.expires_at > now + TOKEN_EXPIRY_BUFFER_S;

            if (isFresh) return session.user.id;

            const {
              data: { session: refreshed },
            } = await supabase.auth.refreshSession();
            return refreshed?.user?.id ?? null;
          })(),
          new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error('Auth timeout')),
              AUTH_TIMEOUT_MS,
            ),
          ),
        ]);

        if (result) {
          set({ isAuthenticated: true });
        }

        return result;
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'ensure_valid_user_id' },
        });
        return null;
      }
    },

    refreshSession: async () => {
      if (!supabase) return;
      const {
        data: { session },
      } = await supabase.auth.refreshSession();
      applySession(session);
    },

    signInWithGoogle: async (redirectTo?: string) => {
      const client = requireClient();
      const { error } = await client.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: redirectTo ?? window.location.origin },
      });
      if (error) throw error;
    },

    signOut: async () => {
      const client = requireClient();
      const { error } = await client.auth.signOut();
      if (error) throw error;
    },

    setProfileUserName: (name: string | null) => set({ profileUserName: name }),
    setProfileUserId: (id: string | null) => set({ profileUserId: id }),
  };
});
